import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, CheckCircle } from "@phosphor-icons/react";

/**
 * Touch-optimierter Signatur-Canvas.
 * Ruft onChange(dataUrl) auf, sobald etwas gezeichnet wurde (oder null nach Reset).
 */
export default function SignaturePad({ onChange, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const point = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="mt-2" data-testid={testid}>
      <div className="relative rounded-xl border-2 border-dashed border-border bg-white overflow-hidden" style={{ touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          className="w-full block"
          style={{ height: 160 }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            Hier mit Finger oder Maus unterschreiben
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          {!empty && <><CheckCircle size={14} weight="fill" className="text-emerald-600"/> Unterschrift erfasst</>}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={clear} className="rounded-lg" data-testid={`${testid}-clear`}>
          <Eraser size={14} weight="bold"/><span className="ml-1">Löschen</span>
        </Button>
      </div>
    </div>
  );
}
